/**
 * uploadProfileSignature — POST /api/associates/associates/upload-signature/.
 * The rep's stored profile signature, set during onboarding and reused
 * for checkout autofill. applyLocal caches the base64 data URL so the
 * profile UI shows it immediately; sync() converts it to a Blob and
 * POSTs as multipart. No dependency.
 */

import type { WriteOp, SyncResult } from '../types';
import { STORES, put } from '../../utils/indexedDB';
import {
  BASE_URL,
  multipartHeaders,
  classifyResponse,
  networkErrorResult,
} from '../httpHelpers';

export interface UploadProfileSignatureInput {
  signatureDataUrl: string; // "data:image/png;base64,XXX"
  signatureSvg?: string;
}

export interface UploadProfileSignatureResponse {
  success: boolean;
}

const PROFILE_SIG_KEY = 'profileSignature';

function dataUrlToBlob(dataUrl: string): Blob {
  const [meta, b64] = dataUrl.split(',');
  const match = meta.match(/^data:(.+?);base64$/);
  const mime = match ? match[1] : 'image/png';
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

export const uploadProfileSignatureOp: WriteOp<
  UploadProfileSignatureInput,
  UploadProfileSignatureInput,
  UploadProfileSignatureResponse
> = {
  kind: 'uploadProfileSignature',

  async applyLocal(input) {
    await put(STORES.VISIT_SINGLETONS, {
      key: PROFILE_SIG_KEY,
      signatureDataUrl: input.signatureDataUrl,
      signatureSvg: input.signatureSvg,
      updatedAt: new Date().toISOString(),
      syncStatus: 'pending',
    });
  },

  serialize(input, localId) {
    return {
      action: 'uploadProfileSignature',
      endpoint: '/api/associates/associates/upload-signature/',
      method: 'POST',
      payload: input,
      priority: 70,
      maxRetries: 5,
      dependsOn: null,
      relatedTaskUUID: localId,
    };
  },

  async sync(row): Promise<SyncResult> {
    const input = row.payload as UploadProfileSignatureInput;
    const blob = dataUrlToBlob(input.signatureDataUrl);
    const formData = new FormData();
    formData.append('signature', blob, 'signature.png');
    if (input.signatureSvg) formData.append('signature_svg', input.signatureSvg);

    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${row.endpoint}`, {
        method: row.method,
        headers: multipartHeaders(),
        body: formData,
      });
    } catch (err) {
      return networkErrorResult(err);
    }

    const classified = await classifyResponse(response);
    if (classified) return classified;
    return { status: 'success', retriable: false };
  },

  async reconcile(row) {
    const input = row.payload as UploadProfileSignatureInput;
    await put(STORES.VISIT_SINGLETONS, {
      key: PROFILE_SIG_KEY,
      signatureDataUrl: input.signatureDataUrl,
      signatureSvg: input.signatureSvg,
      updatedAt: new Date().toISOString(),
      syncStatus: 'synced',
    });
  },

  dependsOn() {
    return null;
  },
};

export type UploadProfileSignatureOp = typeof uploadProfileSignatureOp;
